import React, { useState } from 'react';
import type { FC } from 'react';
import { stripEdgePunctuation, type VocabEntry } from '../articles/vocab-store';
import { formatRelativeDate } from '../../utils/date-format';
import { useTranslation } from '../../i18n/I18nContext';
import { IconButton } from '../../components/ui/icon-button';
import { Badge } from '../../components/ui/badge';
import { buildTables, type InflectionTable } from './inflection-tables';

interface VocabEntryCardProps {
  entry: VocabEntry;
  onDelete: () => void;
}

function renderContext(context: string, word: string): React.ReactElement {
  const lowerContext = context.toLowerCase();
  const lowerWord = stripEdgePunctuation(word.toLowerCase());
  const index = lowerContext.indexOf(lowerWord);

  if (index === -1) {
    return <span>{context}</span>;
  }

  const before = context.slice(0, index);
  const match = context.slice(index, index + lowerWord.length);
  const after = context.slice(index + lowerWord.length);
  const isPhrase = word.trim().includes(' ');
  const colorClass = isPhrase
    ? 'bg-purple text-purple-foreground'
    : 'bg-cyan text-cyan-foreground';

  return (
    <span>
      {before}
      <span className={`inline rounded px-1.5 py-0.5 ${colorClass}`}>{match}</span>
      {after}
    </span>
  );
}

/**
 * Renders one inflection table. The wrapper and the table itself both size
 * to content (`w-fit` / `table-auto`) so columns don't carry blank space
 * when forms are short. Long forms still expand naturally.
 */
const InflectionTableView: FC<{ table: InflectionTable }> = ({ table }) => {
  const showHeader = table.columns.some(c => c !== '');

  return (
    <div className="border border-border rounded p-2 w-fit">
      {table.title && (
        <div className="text-[11px] font-semibold text-muted-foreground mb-1">
          {table.title}
        </div>
      )}
      <table className="text-xs table-auto">
        {showHeader && (
          <thead>
            <tr className="text-[10px] text-muted-foreground/80 uppercase">
              <th className="text-left pr-3 font-normal pb-0.5"></th>
              {table.columns.map((col, i) => (
                <th key={i} className="text-left pr-3 font-normal pb-0.5">{col}</th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {table.rows.map((row, i) => (
            <tr key={`${row.label}-${i}`}>
              <td className="text-muted-foreground py-0.5 pr-3 whitespace-nowrap">
                {row.label}
              </td>
              {row.cells.map((cell, j) => (
                <td key={j} className="text-foreground font-medium py-0.5 pr-3 whitespace-nowrap">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const VocabEntryCard: FC<VocabEntryCardProps> = ({ entry, onDelete }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const grammarTags: { label: string; value: string; color: string }[] = [];
  if (entry.partOfSpeech) grammarTags.push({ label: t('grammar.pos'), value: entry.partOfSpeech, color: 'bg-pink text-pink-foreground' });
  if (entry.grammarCase) grammarTags.push({ label: t('grammar.case'), value: entry.grammarCase, color: 'bg-info text-info-foreground' });
  if (entry.gender) grammarTags.push({ label: t('grammar.gender'), value: entry.gender, color: 'bg-teal text-teal-foreground' });
  if (entry.number) grammarTags.push({ label: t('grammar.number'), value: entry.number, color: 'bg-success text-success-foreground' });
  if (entry.tense) grammarTags.push({ label: t('grammar.tense'), value: entry.tense, color: 'bg-warning text-warning-foreground' });
  if (entry.mood) grammarTags.push({ label: t('grammar.mood'), value: entry.mood, color: 'bg-amber text-amber-foreground' });
  if (entry.voice) grammarTags.push({ label: t('grammar.voice'), value: entry.voice, color: 'bg-orange text-orange-foreground' });
  if (entry.aspect) grammarTags.push({ label: t('grammar.aspect'), value: entry.aspect, color: 'bg-muted text-muted-foreground' });

  const showNormalized = entry.normalizedForm && entry.normalizedForm !== entry.text;
  const hasContext = entry.context && entry.context.trim() !== entry.text.trim();

  const inflectionTables = entry.inflections ? buildTables(entry.inflections) : [];
  const hasInflections = inflectionTables.length > 0;

  return (
    <div className="border border-border rounded-lg bg-card overflow-hidden">
      <div className="px-3 py-2.5">
        {/* Metadata row */}
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-indigo text-indigo-foreground" title={t('badge.source_language')}>
              {entry.sourceLanguage.toUpperCase()}
            </span>
            {grammarTags.slice(0, 3).map((tag) => (
              <span
                key={tag.label}
                className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${tag.color}`}
                title={tag.label}
              >
                {tag.value}
              </span>
            ))}
            <Badge variant="secondary" size="sm" title={t('badge.date')}>{formatRelativeDate(entry.createdAt, t)}</Badge>
          </div>
          <div className="flex items-center gap-0.5">
            {hasInflections && (
              <IconButton
                onClick={() => setExpanded(!expanded)}
                size="card"
                title={expanded ? t('vocab.collapse') : t('vocab.expand')}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  className={`transition-transform ${expanded ? 'rotate-180' : ''}`}
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </IconButton>
            )}
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              size="card"
              title={t('common.delete')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18" /><path d="m6 6 12 12" />
              </svg>
            </IconButton>
          </div>
        </div>

        {/* Text + translation inline */}
        <div className="flex items-baseline gap-1.5 flex-wrap break-words min-w-0">
          <span className="font-semibold text-foreground">{entry.text}</span>
          {showNormalized && (
            <span className="text-sm text-muted-foreground">({entry.normalizedForm})</span>
          )}
          <span className="text-muted-foreground">&rsaquo;&rsaquo;</span>
          <span className="italic text-foreground">{entry.translation}</span>
        </div>

        {/* Context sentence with selection-style highlight — always visible */}
        {hasContext && (
          <p className="mt-1 text-sm text-foreground leading-normal">
            {renderContext(entry.context!, entry.text)}
          </p>
        )}

        {/* Inflection drop-down — only when entry has inflection forms.
             Nouns get one wide case×number table; verbs get multiple small
             per-tense tables side by side that wrap on narrow cards. */}
        {expanded && hasInflections && (
          <div className="mt-4">
            <div className="flex flex-wrap gap-3">
              {inflectionTables.map(table => (
                <InflectionTableView key={table.title} table={table} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VocabEntryCard;
